import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'
import { PDFDownloadLink } from '@react-pdf/renderer'
import ScenarioPDF from '../components/ScenarioPDF'

interface Deal {
  id: number
  title: string
  amount: string
  customer_name: string
}

interface Scenario {
  id?: number
  deal_id?: number
  scenario_number: number
  bank?: string
  type?: string
  loan_amount?: number
  insured?: boolean
  insurance_rate?: number
  interest_rate?: number
  term?: number
  payment_frequency?: string
  payment?: number
  comments?: string
  created_at?: string
  updated_at?: string
}

interface Bank {
  id: number
  name: string
  position: number
  is_active: boolean
}

interface PaymentFrequency {
  id: number
  name: string
  periods_per_year: number
  is_accelerated: boolean
  position: number
  is_active: boolean
}

interface DealType {
  id: number
  name: string
  position: number
  is_active: boolean
}

const ScenarioBuilder: React.FC = () => {
  const { dealId } = useParams<{ dealId: string }>()
  const [deal, setDeal] = useState<Deal | null>(null)
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [banks, setBanks] = useState<Bank[]>([])
  const [paymentFrequencies, setPaymentFrequencies] = useState<PaymentFrequency[]>([])
  const [dealTypes, setDealTypes] = useState<DealType[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<{ [key: number]: boolean }>({})

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        const [dealResponse, scenariosResponse, configResponse] = await Promise.all([
          makeApiCall(`api/deals/${dealId}`),
          makeApiCall(`api/scenarios?deal_id=${dealId}`),
          makeApiCall(`api/scenarios/config`)
        ])

        if (!dealResponse.ok) {
          throw new Error('Failed to fetch deal data')
        }

        if (!scenariosResponse.ok) {
          throw new Error('Failed to fetch scenarios data')
        }

        if (!configResponse.ok) {
          throw new Error('Failed to fetch configuration data')
        }

        const [dealData, scenariosData, configData] = await Promise.all([
          dealResponse.json(),
          scenariosResponse.json(),
          configResponse.json()
        ])

        setDeal(dealData)
        setBanks(configData.banks || [])
        setPaymentFrequencies(configData.paymentFrequencies || [])
        setDealTypes(configData.dealTypes || [])

        // Merge existing scenarios with empty scenarios
        const existingScenarios = scenariosData.scenarios || []
        const updatedScenarios = [1, 2, 3].map(num => {
          const existing = existingScenarios.find((s: Scenario) => s.scenario_number === num)
          return existing || createEmptyScenario(num)
        })

        setScenarios(updatedScenarios)
      } catch (err) {
        console.error('Error fetching data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load data')
      } finally {
        setLoading(false)
      }
    }

    if (dealId) {
      fetchData()
    }
  }, [dealId])

  const createEmptyScenario = (scenarioNumber: number): Scenario => {
    return {
      deal_id: parseInt(dealId!),
      scenario_number: scenarioNumber,
      bank: '',
      type: '',
      loan_amount: 0,
      insured: false,
      insurance_rate: 0,
      interest_rate: 0,
      term: 25,
      payment_frequency: '',
      payment: 0,
      comments: ''
    }
  }

  const updateScenario = (scenarioNumber: number, field: keyof Scenario, value: string | number | boolean) => {
    setScenarios(prev => prev.map(s => 
      s.scenario_number === scenarioNumber ? { ...s, [field]: value } : s
    ))
  }

  const saveScenario = async (scenario: Scenario) => {
    setSaving(prev => ({ ...prev, [scenario.scenario_number]: true }))

    try {
      const response = await makeJsonApiCall('api/scenarios', 'POST', {
        deal_id: dealId,
        scenario_number: scenario.scenario_number,
        bank: scenario.bank,
        type: scenario.type,
        loan_amount: scenario.loan_amount,
        insured: scenario.insured,
        insurance_rate: scenario.insurance_rate,
        interest_rate: scenario.interest_rate,
        term: scenario.term,
        payment_frequency: scenario.payment_frequency,
        comments: scenario.comments
      })

      if (!response.ok) {
        throw new Error('Failed to save scenario')
      }

      const data = await response.json()
      setScenarios(prev => prev.map(s => 
        s.scenario_number === scenario.scenario_number ? data : s
      ))
    } catch (error) {
      console.error('Error saving scenario:', error)
      setError(error instanceof Error ? error.message : 'Failed to save scenario')
    } finally {
      setSaving(prev => ({ ...prev, [scenario.scenario_number]: false }))
    }
  }

  const deleteScenario = async (scenarioNumber: number) => {
    try {
      const scenario = scenarios.find(s => s.scenario_number === scenarioNumber)
      if (scenario?.id) {
        const response = await makeApiCall(`api/scenarios/${scenario.id}`, {
          method: 'DELETE'
        })

        if (!response.ok) {
          throw new Error('Failed to delete scenario')
        }
      }

      setScenarios(prev => prev.map(s => 
        s.scenario_number === scenarioNumber ? createEmptyScenario(scenarioNumber) : s
      ))
    } catch (error) {
      console.error('Error deleting scenario:', error)
      setError(error instanceof Error ? error.message : 'Failed to delete scenario')
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading scenarios...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">Error Loading Scenarios</h3>
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    )
  }

  if (!deal) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
          <p className="text-yellow-800 dark:text-yellow-200">Deal not found</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Scenario Builder
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {deal.title} - {deal.customer_name}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {scenarios.map(scenario => (
          <div key={scenario.scenario_number} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Scenario {scenario.scenario_number}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bank
                </label>
                <select
                  value={scenario.bank || ''}
                  onChange={(e) => updateScenario(scenario.scenario_number, 'bank', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select a bank</option>
                  {banks.map(bank => (
                    <option key={bank.id} value={bank.name}>{bank.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type
                </label>
                <select
                  value={scenario.type || ''}
                  onChange={(e) => updateScenario(scenario.scenario_number, 'type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select type</option>
                  {dealTypes.map(type => (
                    <option key={type.id} value={type.name}>{type.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Loan Amount
                </label>
                <input
                  type="number"
                  value={scenario.loan_amount || ''}
                  onChange={(e) => updateScenario(scenario.scenario_number, 'loan_amount', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="0"
                />
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={scenario.insured || false}
                    onChange={(e) => updateScenario(scenario.scenario_number, 'insured', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Insured</span>
                </label>
              </div>

              {scenario.insured && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Insurance Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    value={scenario.insurance_rate || ''}
                    onChange={(e) => updateScenario(scenario.scenario_number, 'insurance_rate', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0.000"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Interest Rate (%)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  value={scenario.interest_rate || ''}
                  onChange={(e) => updateScenario(scenario.scenario_number, 'interest_rate', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="0.0000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Term (Years)
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={scenario.term || ''}
                  onChange={(e) => updateScenario(scenario.scenario_number, 'term', parseInt(e.target.value) || 25)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="25"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Payment Frequency
                </label>
                <select
                  value={scenario.payment_frequency || ''}
                  onChange={(e) => updateScenario(scenario.scenario_number, 'payment_frequency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select frequency</option>
                  {paymentFrequencies.map(freq => (
                    <option key={freq.id} value={freq.name}>{freq.name}</option>
                  ))}
                </select>
              </div>

              {scenario.payment && scenario.payment > 0 && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <div className="text-sm font-medium text-green-800 dark:text-green-200">
                    Calculated Payment: {formatCurrency(scenario.payment)}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Comments
                </label>
                <textarea
                  value={scenario.comments || ''}
                  onChange={(e) => updateScenario(scenario.scenario_number, 'comments', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  placeholder="Add any notes or comments about this scenario..."
                />
              </div>

              <div className="flex justify-between pt-4">
                <button
                  onClick={() => saveScenario(scenario)}
                  disabled={saving[scenario.scenario_number]}
                  className="px-4 py-2 bg-gray-900 hover:bg-gray-800 disabled:bg-blue-400 text-white rounded-md text-sm font-medium transition-colors"
                >
                  {saving[scenario.scenario_number] ? 'Saving...' : 'Save & Calculate'}
                </button>
                <button
                  onClick={() => deleteScenario(scenario.scenario_number)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {scenarios.some(s => s.payment && s.payment > 0) && (
        <div className="mt-6">
          <PDFDownloadLink
            document={<ScenarioPDF deal={deal} scenarios={scenarios} />}
            fileName={`${deal.title}-scenarios.pdf`}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors"
          >
            {({ loading }) => (loading ? 'Generating PDF...' : 'Download PDF')}
          </PDFDownloadLink>
        </div>
      )}
    </div>
  )
}

export default ScenarioBuilder 